import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { X, AlertCircle } from 'lucide-react';
import { Notification } from '../../types/communication';

interface EmergencyPopupOverlayProps {
  notification: Notification | null;
  onDismiss: (notificationId: string, dontShowAgain?: boolean) => void;
}

export const EmergencyPopupOverlay: React.FC<EmergencyPopupOverlayProps> = ({
  notification,
  onDismiss,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!notification) return null;

  const handleDismiss = () => {
    onDismiss(notification.id, dontShowAgain);
  };

  return (
    <Dialog
      open={true}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: '24px',
          p: 0,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            position: 'relative',
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={handleDismiss}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            <X size={20} />
          </IconButton>

          {/* Emergency banner */}
          <Box
            sx={{
              bgcolor: '#EF4444',
              color: 'white',
              py: 1.5,
              px: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AlertCircle size={20} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Urgent School Notification
            </Typography>
          </Box>

          {/* Main content */}
          <Box
            sx={{
              bgcolor: '#6366F1',
              color: 'white',
              p: 5,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background emoji */}
            <Box
              sx={{
                position: 'absolute',
                right: -40,
                bottom: -40,
                fontSize: '12rem',
                opacity: 0.1,
                pointerEvents: 'none',
              }}
            >
              📢
            </Box>

            {/* Content */}
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  mb: 3,
                  lineHeight: 1.2,
                }}
              >
                {notification.title}
              </Typography>

              <Typography
                sx={{
                  fontSize: '1.125rem',
                  lineHeight: 1.7,
                  opacity: 0.95,
                  mb: 4,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {notification.message}
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={handleDismiss}
                sx={{
                  bgcolor: 'white',
                  color: '#6366F1',
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: '#F9FAFB',
                  },
                }}
              >
                I Understand
              </Button>

              {/* Don't show again option */}
              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dontShowAgain}
                      onChange={(e) => setDontShowAgain(e.target.checked)}
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        '&.Mui-checked': {
                          color: 'white',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '0.875rem', opacity: 0.8 }}>
                      Don't show this again
                    </Typography>
                  }
                />
              </Box>
            </Box>
          </Box>

          {/* Footer metadata */}
          <Box
            sx={{
              bgcolor: '#F9FAFB',
              px: 3,
              py: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
              Sent by School Administration
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
              {new Date(notification.createdAt).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
